import React, { useEffect, useState, useMemo } from "react";
import instance from "../instance";
import { useNavigate } from "react-router-dom";
import CustomerSearch from "./Customers/CustomerSearch";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const UniqueProducts = () => {
  const formatDateToCustomString = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const [categories, setCategories] = useState([]);
  const [editingProductId, setEditingProductId] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [isLoading, setIsLoading] = useState(false);


  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [excludeTerm, setExcludeTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: "AddedDate", direction: "descending" });

  const navigate = useNavigate();

  const fetchData = async () => {
    try {
      const response = await instance.get("products/unique");
      setData(response.data.data);
      setLoading(false);
    } catch (error) {
      setError(error.message);
      setLoading(false);
    }
  };
  const fetchCategories = async () => {
    try {
      const response = await instance.get("products/categories");
      setCategories(response.data.data);
    } catch (error) {
      console.error("Error fetching categories:", error);
      toast.error("Failed to load categories");
    }
  };

  useEffect(() => {
    fetchData();
    fetchCategories();
  }, []);


  const handleSort = (column) => {
    const direction = sortConfig.direction === "ascending" ? "descending" : "ascending";
    setSortConfig({ key: column, direction });
  };

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleExcludeSearch = (event) => {
    setExcludeTerm(event.target.value);
  };

  const resetSearch = () => {
    setSearchTerm("");
    setExcludeTerm("");
  };

  const sortedData = useMemo(() => {
    let sorted = [...data];
    sorted = sorted.filter((item) => {
      const searchTermLower = searchTerm.toLowerCase();
      const excludeTermLower = excludeTerm.toLowerCase();
      const itemValues = [
        item.ID,
        item.Products,
        formatDateToCustomString(item.AddedDate),
        item.CategoryName,
      ].map((value) => (value ? value.toString().toLowerCase() : ""));
      const matchesSearchTerm =
        searchTerm === "" ||
        itemValues.some((value) => value.includes(searchTermLower));
      const matchesExcludeTerm =
        excludeTerm === "" ||
        !itemValues.some((value) => value.includes(excludeTermLower));
      return matchesSearchTerm && matchesExcludeTerm;
    });

    sorted.sort((a, b) => {
      let aValue, bValue;
      
      if (sortConfig.key === "AddedDate") {
        aValue = new Date(a[sortConfig.key]);
        bValue = new Date(b[sortConfig.key]);
      } else if (sortConfig.key === "CategoryName") {
        // التعامل مع القيم كنصوص للمقارنة
        aValue = a[sortConfig.key]?.toString().toLowerCase() || "";
        bValue = b[sortConfig.key]?.toString().toLowerCase() || "";
      } else {
        aValue = a[sortConfig.key];
        bValue = b[sortConfig.key];
      }
      
      if (aValue < bValue) {
        return sortConfig.direction === "ascending" ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === "ascending" ? 1 : -1;
      }
      return 0;
    });

    return sorted.map((item, index) => ({ ...item, sequence: index + 1 }));
  }, [data, sortConfig, searchTerm, excludeTerm]);

  if (error) {
    return <div>Error: {error}</div>;
  }

  const startEditing = (productId, currentCategoryId) => {
    setEditingProductId(productId);
    setSelectedCategory(currentCategoryId || "");
  };

  // دالة لإلغاء التحرير
  const cancelEditing = () => {
    setEditingProductId(null);
    setSelectedCategory("");
  };

  // دالة لحفظ التصنيف الجديد
  const saveCategory = async (productId) => {
    setIsLoading(true);
    try {
      await instance.put(`products/update-category/${productId}`, {
        CategoryID: selectedCategory
      });
      
      // تحديث البيانات المحلية بعد التحديث الناجح
      setData(data.map(item => 
        item.ID === productId 
          ? { 
              ...item, 
              CategoryID: selectedCategory,
              CategoryName: categories.find(c => c.CategoryID.toString() === selectedCategory.toString())?.CategoryName || ""
            } 
          : item
      ));
      
      toast.success("Category updated successfully");
      setEditingProductId(null);
    } catch (error) {
      console.error("Error updating category:", error);
      toast.error("Failed to update category");
    } finally {
      setIsLoading(false);
    }
  };


  return (
    <div>
      <header>
        <h1>Products</h1>
      </header>
      <div style={{ textAlign: "center" }}>
        <button onClick={() => navigate("/")} className="button">
          ← Main
        </button>
      </div>
      <CustomerSearch
        searchTerm={searchTerm}
        excludeTerm={excludeTerm}
        datalistData={data.map((item) => item.Products)}
        handleSearch={handleSearch}
        handleExcludeSearch={handleExcludeSearch}
        resetSearch={resetSearch}
        dateFilter={null}
        formatDateRange={() => ""}
      />
      <table id="unique-products-table">
        <thead>
          <tr>
            <th className="sortable" onClick={() => handleSort("sequence")}>
              #
            </th>
            <th className="sortable" onClick={() => handleSort("ID")}>
              ID
            </th>
            <th className="sortable" onClick={() => handleSort("Products")}>
              Products
            </th>
            <th className="sortable" onClick={() => handleSort("AddedDate")}>
              Added Date
            </th>
            <th className="sortable" onClick={() => handleSort("CategoryName")}>
              Category
            </th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody >
          {loading ? (
            <tr>
              <td colSpan="6">Loading...</td>
            </tr>
          ) : sortedData.length === 0 ? (
            <tr>
              <td colSpan="6">No data available</td>
            </tr>
          ) : (
            sortedData.map((item) => (
              <tr key={item.sequence}>
                <td>{item.sequence}</td>
                <td>{item.ID}</td>
                <td>{item.Products}</td>
                <td>{formatDateToCustomString(item.AddedDate)}</td>
                <td>
                  {editingProductId === item.ID ? (
                    <select 
                      value={selectedCategory} 
                      onChange={(e) => setSelectedCategory(e.target.value)}
                    >
                      <option value="">--Select Product--</option>
                      {categories.map(category => (
                        <option 
                          key={category.CategoryID} 
                          value={category.CategoryID}
                        >
                          {category.CategoryName}
                        </option>
                      ))}
                    </select>
                  ) : (
                    item.CategoryName || "-"
                  )}
                </td>
                <td>
                  {editingProductId === item.ID ? (
                    <>
                      <button 
                        onClick={() => saveCategory(item.ID)} 
                        disabled={isLoading}
                        className="save-btn"
                      >
                        {isLoading ? " Save..." : "Save"}
                      </button>
                      <button 
                        onClick={cancelEditing}
                        className="cancel-btn"
                      >
                        Cancel
                      </button>
                    </>
                  ) : (
                    <button 
                      onClick={() => startEditing(item.ID, item.CategoryID)}
                      className="edit-btn"
                    >
                      Update
                    </button>
                  )}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
      <footer>
        <span>© 2025 OwlTech All rights reserved.</span>
      </footer>
    </div>
  );
};

export default UniqueProducts;